import {useEffect} from 'react';

import {recordRumActionOncePerView} from '../../util/datadog/datadogRum';

/**
 * Per-view panel availability signals for the panel SLO.
 *
 * Both actions fire at most once per RUM view per panel type, so a view with
 * fifty broken media panels counts as one bad view rather than fifty bad
 * panels. `errorKind` is the first failure seen in the view and is a tag, not
 * part of the dedupe key, so a view can never emit two "Panel errored" actions
 * for the same panel type. The SLI is `1 - (views with "Panel errored") /
 * (views with "Panel rendered")`, filtered by `panelViewType`.
 */
const PANEL_RENDERED_ACTION = 'Panel rendered';
const PANEL_ERRORED_ACTION = 'Panel errored';

export type PanelErrorKind =
  /** An exception reached the panel error boundary. */
  | 'render_crash'
  /** Legacy / react fallback when failure is not classified further. */
  | 'media_fetch_failed'
  /** The run has no file path logged for the requested media step. */
  | 'media_missing_data'
  /** AvailableFiles row exists but upload is not complete (`sizeBytes === 0`). */
  | 'media_upload_pending'
  /** The backend returned no download URL for a logged media file. */
  | 'media_missing_url'
  /** AvailableFiles GraphQL query failed on the react-fetch path. */
  | 'media_query_failed'
  /** Signed-URL blob download failed (network / HTTP, excluding 403). */
  | 'media_blob_fetch_failed'
  /** Signed-URL blob download rejected (typically HTTP 403). */
  | 'media_blob_unauthorized'
  /** Signed URL rejected as expired and a refreshed URL also failed. */
  | 'media_blob_url_expired';

export function recordPanelRendered(panelViewType: string): void {
  recordRumActionOncePerView(
    PANEL_RENDERED_ACTION,
    {panelViewType},
    `${PANEL_RENDERED_ACTION}:${panelViewType}`
  );
}

/** Records the rendered action after commit, once the panel type is known. */
export function useRecordPanelRendered(panelViewType: string | undefined) {
  useEffect(() => {
    if (panelViewType != null) {
      recordPanelRendered(panelViewType);
    }
  }, [panelViewType]);
}

export function recordPanelErrored(
  panelViewType: string,
  errorKind: PanelErrorKind
): void {
  recordRumActionOncePerView(
    PANEL_ERRORED_ACTION,
    {panelViewType, errorKind},
    `${PANEL_ERRORED_ACTION}:${panelViewType}`
  );
}

/** Records the errored action after commit whenever a handled failure is shown. */
export function useRecordPanelErrored(
  panelViewType: string,
  errorKind: PanelErrorKind | null
) {
  useEffect(() => {
    if (errorKind != null) {
      recordPanelErrored(panelViewType, errorKind);
    }
  }, [panelViewType, errorKind]);
}
